// Admin v2 — Admin Chat (Figma ADM_06 + 06b).
// Üzenetek fül: bármely felhasználó beszélgetései (a mobilapp conversations/messages sémája szerint),
// az admin a kiválasztott felhasználónak közvetlenül írhat. Admin chat fül: közös admin csatorna
// (conversations/admin_channel) és admin–admin privát szálak.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc, collection, doc, DocumentData, getDoc, getDocs, increment, limit, onSnapshot, query, setDoc, Timestamp, updateDoc, UpdateData, where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { AdminUser, normalizeUser, roleOf, ROLE_LABEL, timeAgo, toDate } from '../data';
import { Avatar, Badge, Empty, Input, PageHeader, Spinner, Tabs, useToast } from '../ui';

const CHANNEL_ID = 'admin_channel';

interface Conv { id: string; participantIds: string[]; names: Record<string, string>; last: string; at: Date | null; isGroup: boolean; groupName: string; unread: Record<string, number> }
interface Msg { id: string; senderId: string; senderName: string; text: string; at: Date | null; matchId: string | null; bookingId: string | null; imageUrl: string | null }

const convFrom = (id: string, v: Record<string, unknown>): Conv => ({
  id,
  participantIds: (v.participantIds as string[]) ?? [],
  names: (v.participantNames as Record<string, string>) ?? {},
  last: String(v.lastMessage ?? ''),
  at: toDate(v.lastMessageTime),
  isGroup: v.isGroup === true,
  groupName: String(v.groupName ?? ''),
  unread: (v.unreadCount as Record<string, number>) ?? {},
});

const hm = (d: Date | null) => {
  if (!d) return '';
  const today = new Date().toDateString() === d.toDateString();
  return today ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : timeAgo(d);
};

/** Üzenet küldése egy beszélgetésbe a mobilapp sémájával (unreadCount növelése a többieknek). */
async function sendTo(conv: { id: string; participantIds: string[] }, me: { uid: string; name: string }, text: string) {
  const others = conv.participantIds.filter((p) => p !== me.uid);
  await addDoc(collection(db, 'messages'), {
    conversationId: conv.id, senderId: me.uid, senderName: me.name, receiverId: others.length === 1 ? others[0] : null,
    text, timestamp: Timestamp.now(), isRead: false, isAdminMessage: true,
  });
  const upd: UpdateData<DocumentData> = { lastMessage: text, lastMessageTime: Timestamp.now(), lastMessageSenderId: me.uid };
  others.forEach((o) => { upd[`unreadCount.${o}`] = increment(1); });
  await updateDoc(doc(db, 'conversations', conv.id), upd);
}

/** Admin ↔ felhasználó (vagy admin ↔ admin) páros szál megkeresése vagy létrehozása. */
async function pairConversation(me: { uid: string; name: string }, other: { uid: string; name: string }) {
  const ids = [me.uid, other.uid].sort();
  const hit = await getDocs(query(collection(db, 'conversations'), where('participantIds', '==', ids), limit(1)));
  if (!hit.empty) return { id: hit.docs[0].id, participantIds: ids };
  const ref = doc(collection(db, 'conversations'));
  await setDoc(ref, {
    participantIds: ids,
    participantNames: { [me.uid]: me.name, [other.uid]: other.name },
    lastMessage: '', lastMessageTime: Timestamp.now(), lastMessageSenderId: me.uid,
    unreadCount: { [me.uid]: 0, [other.uid]: 0 },
    acceptedBy: { [me.uid]: true, [other.uid]: true }, // admin üzenete ne kérelemként érkezzen
    isGroup: false, createdBy: me.uid,
  });
  return { id: ref.id, participantIds: ids };
}

export default function AdminChatPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'messages' | 'admins'>('messages');
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  useEffect(() => {
    getDocs(collection(db, 'users')).then((s) => setUsers(s.docs.map((d) => normalizeUser(d.id, d.data()))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'hu')))).catch(() => setUsers([]));
  }, []);
  const meUser = users?.find((u) => u.uid === user?.uid);
  const me = { uid: user?.uid ?? '', name: meUser?.username || meUser?.displayName || 'Admin' };

  return (
    <div>
      <PageHeader title="Admin Chat" sub="Összes felhasználói beszélgetés megtekintése és kezelése" />
      <Tabs value={tab} onChange={setTab} options={[{ value: 'messages', label: 'Üzenetek' }, { value: 'admins', label: 'Admin chat' }]} />
      {users === null ? <Spinner /> : tab === 'messages'
        ? <UserConversations users={users} me={me} />
        : <AdminChannel admins={users.filter((u) => u.isAdmin)} me={me} />}
    </div>
  );
}

/* ---------- Üzenetek (ADM_06) ---------- */
function UserConversations({ users, me }: { users: AdminUser[]; me: { uid: string; name: string } }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<AdminUser | null>(null);
  const [convs, setConvs] = useState<Conv[] | null>(null);
  const [conv, setConv] = useState<Conv | null>(null);
  const byUid = useMemo(() => new Map(users.map((u) => [u.uid, u])), [users]);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return users.filter((u) => !t || [u.displayName, u.username, u.email].some((f) => f.toLowerCase().includes(t))).slice(0, 80);
  }, [users, q]);

  useEffect(() => {
    setConv(null);
    if (!sel) { setConvs(null); return; }
    setConvs(null);
    return onSnapshot(query(collection(db, 'conversations'), where('participantIds', 'array-contains', sel.uid), limit(50)),
      (s) => setConvs(s.docs.map((d) => convFrom(d.id, d.data())).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0))),
      () => setConvs([]));
  }, [sel]);

  const partnerName = (c: Conv) => {
    if (c.isGroup) return c.groupName || 'Csoport';
    const other = c.participantIds.find((p) => p !== sel?.uid) ?? '';
    return c.names[other] || byUid.get(other)?.displayName || 'Ismeretlen';
  };
  const nameOf = (uid: string) => byUid.get(uid)?.displayName || conv?.names[uid] || 'Ismeretlen';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[280px_300px_1fr] gap-4 items-start">
      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl p-3">
        <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Felhasználók keresése..." />
        <div className="mt-2 flex flex-col gap-1 max-h-[560px] overflow-y-auto">
          {list.map((u) => (
            <button key={u.uid} type="button" onClick={() => setSel(u)}
              className={`flex items-center gap-2.5 p-2.5 rounded-xl text-left border transition ${sel?.uid === u.uid ? 'bg-[#00AFF2]/[0.12] border-[#00AFF2]/30' : 'border-transparent hover:bg-white/5'}`}>
              <Avatar src={u.avatarUrl} name={u.displayName} size={32} />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-white truncate">{u.displayName}</span>
                <span className="block text-[12px] text-[#8a8f99] truncate">@{u.username || u.email.split('@')[0]}</span>
              </span>
            </button>
          ))}
          {list.length === 0 && <Empty>Nincs találat.</Empty>}
        </div>
      </div>

      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl p-3">
        <h3 className="px-1 pt-1 pb-2 text-[13px] font-extrabold italic uppercase text-white">Üzenetek</h3>
        {!sel ? <div className="px-1 py-6 text-[13px] text-[#8a8f99]">Válassz egy felhasználót.</div>
          : convs === null ? <Spinner /> : convs.length === 0 ? <div className="px-1 py-6 text-[13px] text-[#8a8f99]">Nincs beszélgetése.</div> : (
            <div className="flex flex-col gap-1 max-h-[560px] overflow-y-auto">
              {convs.map((c) => (
                <button key={c.id} type="button" onClick={() => setConv(c)}
                  className={`flex items-center gap-2.5 p-2.5 rounded-xl text-left transition ${conv?.id === c.id ? 'bg-[#1d232a]' : 'hover:bg-white/5'}`}>
                  <Avatar name={partnerName(c)} size={32} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold text-white truncate">{partnerName(c)}{c.isGroup ? ' (csoport)' : ''}</span>
                      <span className="text-[11px] text-[#8a8f99] shrink-0">{hm(c.at)}</span>
                    </span>
                    <span className="block text-[12px] text-[#8a8f99] truncate">{c.last || '—'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
      </div>

      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl p-4 min-h-[460px] flex flex-col">
        {conv && sel ? (
          <>
            <div className="flex items-center gap-2.5 pb-3 border-b border-[#31383D]">
              <Avatar name={partnerName(conv)} size={30} />
              <div>
                <div className="text-[14px] font-semibold text-white">{sel.displayName} ↔ {partnerName(conv)}</div>
                <div className="text-[12px] text-[#8a8f99]">Üzenet történet</div>
              </div>
            </div>
            <Thread convId={conv.id} mineId={sel.uid} nameOf={nameOf} showNames={conv.isGroup} />
          </>
        ) : <div className="flex-1 flex items-center justify-center text-[13px] text-[#8a8f99]">Válassz egy beszélgetést a megtekintéshez.</div>}
        {sel && (
          <Composer
            placeholder={`Írj üzenetet ${sel.displayName} részére adminként...`}
            onSend={async (t) => {
              try {
                const c = await pairConversation(me, { uid: sel.uid, name: sel.username || sel.displayName });
                await sendTo(c, me, t);
                toast('Üzenet elküldve.');
              } catch (e) {
                console.warn('[chat] küldés', e);
                toast('Az üzenet nem ment el.', 'red');
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ---------- Admin chat (ADM_06b) ---------- */
function AdminChannel({ admins, me }: { admins: AdminUser[]; me: { uid: string; name: string } }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [target, setTarget] = useState<'channel' | AdminUser>('channel');
  const [pair, setPair] = useState<{ id: string; participantIds: string[] } | null>(null);
  const [channelUnread, setChannelUnread] = useState(0);
  const byUid = useMemo(() => new Map(admins.map((u) => [u.uid, u])), [admins]);

  useEffect(() => onSnapshot(doc(db, 'conversations', CHANNEL_ID), (s) => {
    setChannelUnread(s.exists() ? Number((s.data().unreadCount ?? {})[me.uid] ?? 0) : 0);
  }, () => undefined), [me.uid]);

  // megnyitáskor a saját olvasatlan számlálót nullázzuk
  useEffect(() => {
    if (target === 'channel' && channelUnread > 0) {
      updateDoc(doc(db, 'conversations', CHANNEL_ID), { [`unreadCount.${me.uid}`]: 0 }).catch(() => undefined);
    }
  }, [target, channelUnread, me.uid]);

  useEffect(() => {
    setPair(null);
    if (target === 'channel') return;
    const ids = [me.uid, target.uid].sort();
    getDocs(query(collection(db, 'conversations'), where('participantIds', '==', ids), limit(1)))
      .then((s) => setPair(s.empty ? { id: '', participantIds: ids } : { id: s.docs[0].id, participantIds: ids }))
      .catch(() => setPair({ id: '', participantIds: ids }));
  }, [target, me.uid]);

  const others = admins.filter((a) => a.uid !== me.uid)
    .filter((a) => !q.trim() || a.displayName.toLowerCase().includes(q.trim().toLowerCase()));

  const send = async (t: string) => {
    try {
      if (target === 'channel') {
        const ids = admins.map((a) => a.uid).sort();
        const ref = doc(db, 'conversations', CHANNEL_ID);
        const s = await getDoc(ref);
        if (!s.exists()) {
          await setDoc(ref, {
            participantIds: ids, participantNames: Object.fromEntries(admins.map((a) => [a.uid, a.username || a.displayName])),
            isGroup: true, groupName: 'Admin csatorna', lastMessage: '', lastMessageTime: Timestamp.now(), lastMessageSenderId: me.uid,
            unreadCount: {}, acceptedBy: Object.fromEntries(ids.map((i) => [i, true])), createdBy: me.uid,
          });
        } else {
          await updateDoc(ref, { participantIds: ids }); // új adminok is lássák
        }
        await sendTo({ id: CHANNEL_ID, participantIds: ids }, me, t);
      } else {
        const c = pair?.id ? pair : await pairConversation(me, { uid: target.uid, name: target.username || target.displayName });
        if (!pair?.id) setPair(c);
        await sendTo(c, me, t);
      }
    } catch (e) {
      console.warn('[chat] admin küldés', e);
      toast('Az üzenet nem ment el.', 'red');
    }
  };

  const convId = target === 'channel' ? CHANNEL_ID : pair?.id ?? '';
  const title = target === 'channel' ? 'Admin csatorna' : target.displayName;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4 items-start">
      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl p-3">
        <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Adminok keresése..." />
        <div className="mt-2 flex flex-col gap-1">
          <button type="button" onClick={() => setTarget('channel')}
            className={`flex items-center gap-2.5 p-2.5 rounded-xl text-left transition ${target === 'channel' ? 'bg-[#00AFF2]/[0.12]' : 'hover:bg-white/5'}`}>
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#1d232a] border border-[#31383D] text-[#00AFF2]"><Icon name="UsersThree" size={16} /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold text-white">Admin csatorna</span>
              <span className="block text-[12px] text-[#8a8f99]">Mindenki · {admins.length} tag</span>
            </span>
            {channelUnread > 0 && <Badge tone="cyan">{channelUnread}</Badge>}
          </button>
          {others.map((a) => (
            <button key={a.uid} type="button" onClick={() => setTarget(a)}
              className={`flex items-center gap-2.5 p-2.5 rounded-xl text-left transition ${target !== 'channel' && target.uid === a.uid ? 'bg-[#00AFF2]/[0.12]' : 'hover:bg-white/5'}`}>
              <Avatar src={a.avatarUrl} name={a.displayName} size={32} />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-white truncate">{a.displayName}</span>
                <span className="block text-[12px] text-[#8a8f99]">{a.isVenueOperator ? 'Admin · Üzemeltető' : ROLE_LABEL[roleOf(a)] === 'Admin' ? 'Rendszergazda' : ROLE_LABEL[roleOf(a)]}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl p-4 min-h-[460px] flex flex-col">
        <div className="flex items-center justify-between pb-3 border-b border-[#31383D]">
          <h3 className="text-[15px] font-extrabold italic uppercase text-white">{title}</h3>
          {target === 'channel' && <span className="text-[12px] text-[#8a8f99]">{admins.length} tag</span>}
        </div>
        {convId ? <Thread convId={convId} mineId={me.uid} nameOf={(id) => byUid.get(id)?.displayName ?? 'Admin'} showNames={target === 'channel'} />
          : <div className="flex-1 flex items-center justify-center text-[13px] text-[#8a8f99]">Még nincs üzenet. Írj elsőként!</div>}
        <Composer placeholder="Írj egy üzenetet..." onSend={send} />
      </div>
    </div>
  );
}

/* ---------- közös: üzenetszál + beviteli sor ---------- */
function Thread({ convId, mineId, nameOf, showNames }: { convId: string; mineId: string; nameOf: (uid: string) => string; showNames: boolean }) {
  const [msgs, setMsgs] = useState<Msg[] | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setMsgs(null);
    return onSnapshot(query(collection(db, 'messages'), where('conversationId', '==', convId), limit(400)), (s) => {
      setMsgs(s.docs.map((d) => {
        const v = d.data();
        return {
          id: d.id, senderId: String(v.senderId ?? ''), senderName: String(v.senderName ?? ''), text: String(v.text ?? ''),
          at: toDate(v.timestamp), matchId: v.matchId ?? null, bookingId: v.bookingId ?? null, imageUrl: v.imageUrl ?? null,
        };
      }).sort((a, b) => (a.at?.getTime() ?? 0) - (b.at?.getTime() ?? 0)));
    }, () => setMsgs([]));
  }, [convId]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [msgs]);

  if (msgs === null) return <div className="flex-1"><Spinner /></div>;
  return (
    <div className="flex-1 overflow-y-auto max-h-[520px] py-4 flex flex-col gap-3">
      {msgs.length === 0 && <div className="m-auto text-[13px] text-[#8a8f99]">Még nincs üzenet.</div>}
      {msgs.map((m) => {
        const mine = m.senderId === mineId;
        return (
          <div key={m.id} className={`flex items-end gap-2 ${mine ? 'justify-end' : ''}`}>
            {!mine && showNames && <Avatar name={m.senderName || nameOf(m.senderId)} size={26} />}
            <div className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl ${mine ? 'bg-[#00AFF2] text-white' : 'bg-[#1d232a] text-white'}`}>
              {!mine && showNames && <div className="text-[12px] font-semibold text-[#00AFF2] mb-0.5">{m.senderName || nameOf(m.senderId)}</div>}
              {(m.matchId || m.bookingId) && (
                <div className={`mb-1.5 px-3 py-2 rounded-xl border ${mine ? 'border-white/40' : 'border-[#00AFF2]/50 bg-[#15191E]'}`}>
                  <div className={`flex items-center gap-1.5 text-[12px] font-semibold ${mine ? 'text-white' : 'text-[#00AFF2]'}`}>
                    <Icon name={m.matchId ? 'Target' : 'CalendarBlank'} size={14} />{m.matchId ? 'Meccs megosztva' : 'Foglalás megosztva'}
                  </div>
                </div>
              )}
              {m.imageUrl && <img src={m.imageUrl} alt="" className="mb-1.5 rounded-lg max-h-[220px]" />}
              {m.text && <div className="text-[13px] leading-snug whitespace-pre-wrap break-words">{m.text}</div>}
              <div className={`mt-1 text-[10px] ${mine ? 'text-white/80' : 'text-[#8a8f99]'}`}>{hm(m.at)}</div>
            </div>
          </div>
        );
      })}
      <div ref={end} />
    </div>
  );
}

function Composer({ placeholder, onSend }: { placeholder: string; onSend: (t: string) => Promise<void> }) {
  const [t, setT] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const v = t.trim();
    if (!v || busy) return;
    setBusy(true);
    await onSend(v);
    setT('');
    setBusy(false);
  };
  return (
    <form className="flex items-center gap-2 pt-3" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <Input value={t} onChange={(e) => setT(e.target.value)} placeholder={placeholder} className="flex-1" />
      <button type="submit" disabled={busy || !t.trim()}
        className="inline-flex items-center gap-2 h-[40px] px-5 rounded-full bg-[#00AFF2] text-white text-[14px] font-semibold disabled:opacity-50">
        <Icon name="PaperPlaneRight" size={16} />Küldés
      </button>
    </form>
  );
}
